import React from "react"
import clsx from "clsx"
import { isEqual } from "lodash-es"
import { useSelector } from "react-redux"
import { useModal } from "hooks"
import {
  formSubmissionData,
  questionUuidsIdentifyingTaskDepartments as getQuestionUuidsIdentifyingTaskDepartments,
  submissionPermissions,
  submitterFullName as submitterFullNameSelector,
} from "reduxSlices/formSubmissionSlice"
import { getDepartmentFacilityMapping } from "reduxSlices/facilitiesSlice"
import { useFormSubmissionFormContext } from "contexts/FormSubmissionFormContext"
import { lostAnswerQuestions } from "utils/lostAnswerHelpers"
import { CircularIconButton } from "shared/buttons"
import { DEPARTMENT_FACILITY_MAPPING_OPERATOR_DATA_KEY } from "shared/form/QuestionBranchingConditionalCheck/OperatorDataProviders/operatorDataKeys"
import { FormIcon } from "shared/icons"
import ItemRow from "shared/ItemRow"
import { errorToast } from "shared/toast"
import ChangeSubmissionFormTypeModal from "./ChangeSubmissionFormTypeModal"
import EditSubmissionButton from "./EditSubmissionButton"
import EditSubmissionLostAnswersModal from "./EditSubmissionLostAnswersModal"
import EditSubmissionModal from "./EditSubmissionModal"
import EditSubmissionVersionModal from "./EditSubmissionVersionModal"
import FormSubmissionAccessOverview from "./FormSubmissionAccessOverview"
import SubmissionEdit from "./SubmissionEdit"
import SubmissionOverview from "./SubmissionOverview"
import TaskDepartmentWarning from "./TaskDepartmentWarning"

export const SUBMISSION_CONTENT_TAB_ID = "tab-submission-content"
export const SUBMISSION_CONTENT_TAB_PANEL_ID = "tabpanel-submission-content"

const SubmissionContent = () => {
  // If a question is used to identify an assignee/member department
  // in a create-task action on one of the form's workflows,
  // then we want to warn the user that task assigments might need to be updated.
  const {
    modalIsOpen: taskDepartmentWarningIsVisible,
    openModal: showTaskDepartmentWarning,
    closeModal: hideTaskDepartmentWarning,
  } = useModal()

  const {
    modalIsOpen: editSubmissionModalIsOpen,
    openModal: openEditSubmissionModal,
    closeModal: closeEditSubmissionModal,
  } = useModal()

  const {
    modalIsOpen: lostAnswersModalIsOpen,
    openModal: openLostAnswersModal,
    closeModal: closeLostAnswersModal,
  } = useModal()

  const {
    modalIsOpen: editVersionModalIsOpen,
    openModal: openEditVersionModal,
    closeModal: closeEditVersionModal,
  } = useModal()

  const {
    modalIsOpen: changeFormTypeModalIsOpen,
    openModal: openChangeFormTypeModal,
    closeModal: closeChangeFormTypeModal,
  } = useModal()

  const { answers: currentAnswers, isClosed, slug } = useSelector(formSubmissionData)
  const questionUuidsIdentifyingTaskDepartments = useSelector(getQuestionUuidsIdentifyingTaskDepartments)
  const { canEditSubmission } = useSelector(submissionPermissions)
  const submitterFullName = useSelector(submitterFullNameSelector)
  const departmentFacilityMapping = useSelector(getDepartmentFacilityMapping, isEqual)

  const {
    currentFormVersion,
    closeEditMode,
    editMode,
    form,
    loadCurrentFormVersion,
    openEditMode,
    submittedFormVersion,
  } = useFormSubmissionFormContext()
  if (!form.id) return null

  const checkUpdatedAnswers = ({ answers: updatedAnswers }) => {
    const taskDepartmentAnswerChanged = questionUuidsIdentifyingTaskDepartments.some((questionUuid) => (
      !isEqual(updatedAnswers[questionUuid], currentAnswers[questionUuid])
    ))

    if (taskDepartmentAnswerChanged) {
      showTaskDepartmentWarning()
    }
  }

  // A closed form submission cannot be edited, in any way.
  const startEditingSubmission = () => {
    if (isClosed) return

    // Request the current version of the form while the user decides how to edit
    // the submission, since changing answers needs it.
    loadCurrentFormVersion()
    openEditSubmissionModal()
  }

  const changeAnswers = async () => {
    const response = await loadCurrentFormVersion()

    if (!response.ok) {
      errorToast("Something went wrong.  Unable to load the current version of the form.")
      return
    }

    closeEditSubmissionModal()

    // A version is only chosen when the submission was not submitted
    // using the current version of the form.  The version is read from the
    // response, since the loaded version is not in state until a re-render.
    if (response.data.id === submittedFormVersion.id) {
      openEditMode(submittedFormVersion.id)
    } else {
      openEditVersionModal()
    }
  }

  const editSubmittedFormVersion = () => {
    closeEditVersionModal()
    openEditMode(submittedFormVersion.id)
  }

  // The current version of the form is loaded when the edit flow is opened,
  // so there are no answers to account for until then.
  const answersLostOnCurrentFormVersion = currentFormVersion.id
    ? lostAnswerQuestions({
      answers: currentAnswers,
      formVersionUsedInEdit: currentFormVersion,
      operatorData: { [DEPARTMENT_FACILITY_MAPPING_OPERATOR_DATA_KEY]: departmentFacilityMapping },
      submittedFormVersion,
    })
    : []

  const editCurrentFormVersion = () => {
    closeEditVersionModal()

    // A user only confirms the edit when answers would be lost.
    if (answersLostOnCurrentFormVersion.length === 0) {
      openEditMode(currentFormVersion.id)
    } else {
      openLostAnswersModal()
    }
  }

  const confirmLostAnswers = () => {
    closeLostAnswersModal()
    openEditMode(currentFormVersion.id)
  }

  const changeFormType = () => {
    if (isClosed) return

    closeEditSubmissionModal()
    openChangeFormTypeModal()
  }

  // Ensure that the form submission data is in Redux
  // before rendering
  if (!slug) return null

  return (
    <div id={SUBMISSION_CONTENT_TAB_PANEL_ID} className="relative grow" role="tabpanel" aria-labelledby={SUBMISSION_CONTENT_TAB_ID}>
      <FormSubmissionAccessOverview />
      <div className="relative inner-container max-w-screen-lg mx-auto">
        <ItemRow
          itemRowHeaderContentClassName="item-row-header-content-submission-content"
          itemRowHeaderContent={(
            <>
              <div className={clsx("flex flex-row items-center justify-center gap-2 lg:w-full", editMode ? "lg:pl-24" : "lg:pl-[52px]")}>
                <FormIcon form={form} />
                <span className={clsx("font-medium text-xl")}>
                  { form?.title }
                </span>
              </div>
              {
                submitterFullName && (
                  <span className="text-sm text-dark">Submitted by: {submitterFullName}</span>
                )
              }
            </>
          )}
          itemRowHeaderControlsClassName="item-row-header-controls-submission-header-controls"
          itemRowHeaderControls={
            editMode
              ? <CircularIconButton icon="close" onClick={closeEditMode} ariaLabel="exit form submission editing" />
              : (
                canEditSubmission && (
                  <EditSubmissionButton onClick={startEditingSubmission} />)
              )
          }
          itemRowBodyClassName="item-row-body-submission-content"
          itemRowBody={
            editMode
              ? <SubmissionEdit onCancel={closeEditMode} onSuccess={checkUpdatedAnswers} />
              : <SubmissionOverview />
          }
        />
        <EditSubmissionModal
          close={closeEditSubmissionModal}
          isOpen={editSubmissionModalIsOpen}
          onChangeAnswers={changeAnswers}
          onChangeFormType={changeFormType}
        />
        <EditSubmissionVersionModal
          close={closeEditVersionModal}
          isOpen={editVersionModalIsOpen}
          onEditCurrentFormVersion={editCurrentFormVersion}
          onEditSubmittedFormVersion={editSubmittedFormVersion}
        />
        <EditSubmissionLostAnswersModal
          close={closeLostAnswersModal}
          isOpen={lostAnswersModalIsOpen}
          onContinue={confirmLostAnswers}
          questions={answersLostOnCurrentFormVersion}
        />
        <ChangeSubmissionFormTypeModal
          close={closeChangeFormTypeModal}
          isOpen={changeFormTypeModalIsOpen}
        />
        <TaskDepartmentWarning isOpen={taskDepartmentWarningIsVisible} close={hideTaskDepartmentWarning} />
      </div>
    </div>

  )
}

export default SubmissionContent
